import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IAccount, IAccountBlockingReason, ICancelAccountParams } from '@app/shared/models/banking.model';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { AccountBasicService } from '@app/shared/services/banking/account-basic.service';
import { AccountContext } from '@app/shared/services/context/account.context.service';
import { AppContext } from '@app/shared/services/context/app.context.service';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { STATUS } from '@app/shared/types/enums';
import { ACTION_EVENT_PROPERTY, AMPLITUDE_EVENT, ACTION_NAME } from '@app/shared/types/enums/amplitude';
import { catchError, EMPTY, filter, map, Subject, switchMap, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'app-cancel-acc',
  templateUrl: './cancel-acc.component.html',
  styleUrls: ['./cancel-acc.component.sass'],
})
export class CancelAccComponent extends SubscriberComponent implements OnInit, OnDestroy {
  account!: IAccount;
  initialState!: IDynamicDialogConfig;
  public isLoading = false;
  public cancelAccountForm!: FormGroup;
  public reasons!: IAccountBlockingReason[];
  STATUS = STATUS;
  readonly CANCEL_ACCOUNT = 'CANCEL_CONTRACT_ACCOUNT';
  private closeModal$ = new Subject<void>();

  constructor(
    private readonly modalSvc: ModalService,
    private readonly _accBscSvc: AccountBasicService,
    private readonly _accountCtx: AccountContext,
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _customerCtxSvc: CustomerContext,
    private readonly _appCtxSvc: AppContext
  ) {
    super();
  }

  ngOnInit() {
    this.getmodalData();
    this.logInitEvent();
    this.getAccount();
    this.createForm();
    this.initSubscriptions();
    this.setupModalCloseSubscription();
    this.account = this._accountCtx.getCurrentAccount() as IAccount;
  }

  private setupModalCloseSubscription(): void {
    this._subscriptions.sink = this.modalSvc
      .getModalData()
      .pipe(
        takeUntil(this.destroy$),
        filter(data => !!data),
        switchMap(() => this.modalSvc['_dialogRef'].afterClosed())
      )
      .subscribe(() => {
        this.closeModal();
      });
  }

  private logInitEvent(): void {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.CANCEL_ACCOUNT,
    });
  }

  private initSubscriptions(): void {
    this.modalSvc.isPrimaryActionClicked$
      .pipe(
        takeUntil(this.closeModal$),
        filter(value => value),
        switchMap(agentData => {
          const params: ICancelAccountParams = {
            rppUserId: this._customerCtxSvc.getRppUserId(),
            accountCode: this._accountCtx.getCurrentAccount()?.code || '',
            action: this.CANCEL_ACCOUNT,
            reasonId: this.cancelAccountForm.get('reason')?.value.toString() || '',
            userId: this._customerCtxSvc.getUserId(),
            agent_email: this._appCtxSvc.getUserAgentEmail(),
          };

          return this._accBscSvc.cancelAccount(params).pipe(
            catchError(error => {
              this.modalSvc.closeWithErrorMsg(this.initialState.modalComponentData.modalName);

              return EMPTY;
            })
          );
        }),
        tap(() => {
          this.modalSvc.closeWithPendingMsg(this.initialState.modalComponentData.modalName);
        })
      )
      .subscribe();
  }

  getReasons() {
    this._subscriptions.sink = this._customerCtxSvc.contextChanged$.pipe(takeUntil(this.closeModal$)).subscribe({
      next: user => {
        this._accBscSvc.getBlockingReasons(user?.rppUserId || '', 'close').subscribe({
          next: reasons => {
            this.reasons = reasons.data || [];
          },
        });
      },
    });
  }

  getAccount() {
    this._subscriptions.sink = this._accountCtx.accountChanged$.pipe(takeUntil(this.closeModal$)).subscribe({
      next: account => {
        this.account = account as IAccount;
      },
    });
    this.getReasons();
  }

  createForm() {
    this.cancelAccountForm = new FormGroup({
      reason: new FormControl('', Validators.required),
    });
    this.onChangesForm();
  }

  onChangesForm() {
    this._subscriptions.sink = this.cancelAccountForm.valueChanges.pipe(takeUntil(this.closeModal$)).subscribe(value => {
      if (this.cancelAccountForm.valid) {
        const data = {
          ...this.initialState,
          modalComponentData: {
            ...this.initialState.modalComponentData,
            acceptButtonTitleDisabled: false,
            formData: value,
          },
        };

        this.modalSvc.setModalData(data);
      }
    });
  }

  getmodalData() {
    this._subscriptions.sink = this.modalSvc
      .getModalData()
      .pipe(takeUntil(this.closeModal$))
      .subscribe({
        next: (data: IDynamicDialogConfig) => (this.initialState = data),
      });
  }

  closeModal() {
    this.closeModal$.next();
    this.closeModal$.complete();
  }

  ngOnDestroy(): void {
    this.closeModal();
    this.unsubscribe();
  }
}
